import ChevronRight from "@mui/icons-material/ChevronRight";
import Search from "@mui/icons-material/Search";
import {
	Autocomplete,
	Box,
	ListItemButton,
	ListItemText,
	TextField,
	Typography,
} from "@mui/material";

import { PANEL_MAX_HEIGHT, PANEL_WIDTH } from "./constants";
import { FormEntityListboxContext } from "./FormEntityListboxContext";
import { groupIcon, groupKeyFromGroupLabel, groupLabel } from "./group-utils";
import { SlidingListbox } from "./SlidingListbox";
import type { EntityOption, FormEntitySelectProps } from "./types";
import { useFormEntitySelect, type UseFormEntitySelectResult } from "./useFormEntitySelect";

const FormEntitySelect = (props: FormEntitySelectProps) => {
	const {
		label = "Resource",
		fullWidth = true,
		isValueField = false,
	} = props;

	const hookState: UseFormEntitySelectResult = useFormEntitySelect(props);
	const {
		open,
		mainOptions,
		inputValue,
		hasPill,
		value,
		filterOptions,
		getOptionLabel,
		getOptionKey,
		isOptionEqualToValue,
		handleOpen,
		handleClose,
		handleInputChange,
		handleChange,
		handleInputBlur,
		renderPill,
		listboxContextValue,
		suppressNextCloseRef,
		activeTable,
		activeScreenLocation,
		toggleGroupExpand,
		handleTableClick,
		handleScreenLocationClick,
	} = hookState;

	return (
		<FormEntityListboxContext.Provider value={listboxContextValue}>
			<Autocomplete<EntityOption | string, false, boolean, boolean>
				freeSolo={isValueField}
				fullWidth={fullWidth}
				open={open}
				onOpen={handleOpen}
				onClose={handleClose}
				disableClearable
				options={mainOptions}
				slots={{ listbox: SlidingListbox }}
				groupBy={(opt) => {
					if (typeof opt === "string") return "";
					return groupLabel(opt.groupKey);
				}}
				renderGroup={(params) => {
					const gk = groupKeyFromGroupLabel(params.group);
					return (
						<li key={params.key}>
							{gk && (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										px: 2,
										pt: 1.5,
										pb: 0.75,
									}}
								>
									{groupIcon(gk)}
									<Typography
										variant="overline"
										sx={{
											fontWeight: 600,
											fontSize: 10.5,
											letterSpacing: "0.10em",
											textTransform: "uppercase",
											color: "rgba(13, 31, 48, 0.55)",
											lineHeight: 1
										}}
									>
										{params.group}
									</Typography>
								</Box>
							)}
							<ul style={{ padding: 0, margin: 0, listStyle: "none" }}>{params.children}</ul>
						</li>
					);
				}}
				filterOptions={filterOptions}
				getOptionLabel={getOptionLabel}
				getOptionKey={getOptionKey}
				value={value}
				inputValue={hasPill ? "" : inputValue}
				onInputChange={handleInputChange}
				onChange={handleChange}
				isOptionEqualToValue={isOptionEqualToValue}
				clearOnBlur={false}
				selectOnFocus
				handleHomeEndKeys
				blurOnSelect={false}
				noOptionsText="No matching resources"
				sx={{
					minWidth: 0,
					...(fullWidth ? { flex: "1 1 0", width: "auto" } : {}),
					...(hasPill
						? {
								"& .MuiFormControl-root": {
									width: "100%",
									minWidth: 0,
								},
								"& .MuiInputAdornment-root.MuiInputAdornment-positionStart": {
									flex: "1 1 0",
									flexShrink: 1,
									minWidth: 0,
									width: 0,
									maxWidth: "none",
									maxHeight: "none",
								},
							}
						: {}),
				}}
				slotProps={{
					popper: {
						sx: { zIndex: 1500 },
						style: { width: PANEL_WIDTH },
					},
					paper: {
						sx: {
							mt: 0.5,
							borderRadius: 2,
							overflow: "hidden",
							// Neutral hairline + soft elevation instead of the blue
							// 2px border, matching the rest of the dropdown system.
							border: "1px solid rgba(13, 31, 48, 0.08)",
							boxShadow: "0 8px 32px rgba(13, 31, 48, 0.10), 0 1px 3px rgba(13, 31, 48, 0.04)",
							// width: PANEL_WIDTH,
							// minWidth: PANEL_WIDTH,
						},
					},
					listbox: {
						sx: {
							padding: 0,
							maxHeight: PANEL_MAX_HEIGHT,
							minHeight: 0,
						},
					},
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						onBlur={handleInputBlur}
						slotProps={{
							...params.slotProps,
							inputLabel: {
								...params.slotProps.inputLabel,
								shrink: hasPill || open || undefined,
							},
							htmlInput: {
								...params.slotProps.htmlInput,
								readOnly: !isValueField && !open,
								style: { cursor: !isValueField || hasPill ? "pointer" : "text" },
							},
							input: {
								...params.slotProps.input,
								startAdornment: hasPill ? (
									<Box
										component="span"
										sx={{
											display: "flex",
											alignItems: "center",
											alignSelf: "stretch",
											minWidth: 0,
											width: "100%",
											flex: "1 1 0",
											overflow: "hidden",
										}}
										onMouseDown={(e) => e.stopPropagation()}
									>
										{renderPill()}
									</Box>
								) : !isValueField ? (
									<Box
										component="span"
										sx={{ display: "inline-flex", alignItems: "center", mr: 0.5 }}
										onMouseDown={(e) => e.preventDefault()}
									>
										<Search fontSize="small" sx={{ color: "action.active", ml: 0.5 }} />
									</Box>
								) : (
									params.slotProps.input.startAdornment
								),
								endAdornment: params.slotProps.input.endAdornment,
								sx: {
									cursor: "pointer",
									flexWrap: "nowrap",
									overflow: "hidden",
									minWidth: 0,
									width: "100%",
									...(hasPill
										? {
												"& .MuiAutocomplete-endAdornment": {
													flexShrink: 0,
												},
												"& .MuiAutocomplete-input": {
													width: 0,
													minWidth: 0,
													flex: "0 0 0",
													padding: 0,
												},
											}
										: {}),
								},
							},
						}}
						sx={{
							width: "100%",
							minWidth: 0,
							"& .MuiOutlinedInput-root": {
								borderRadius: "5px",
								minWidth: 0,
								width: "100%",
								display: "flex",
								overflow: "hidden",
								...(hasPill
									? {
											"& .MuiInputAdornment-positionStart": {
												flex: "1 1 0",
												flexShrink: 1,
												minWidth: 0,
												width: 0,
												maxWidth: "none",
												marginRight: 0,
												overflow: "hidden",
												maxHeight: "none",
												alignSelf: "stretch",
											},
											"& .MuiInputAdornment-positionStart > *": {
												display: "flex",
												flex: "1 1 0",
												minWidth: 0,
												width: "100%",
												alignSelf: "stretch",
											},
										}
									: {}),
							},
						}}
					/>
				)}
				renderOption={(props, option) => {
					if (typeof option === "string") return null;
					if (option.kind === "showMore") {
						const { key, onClick: _oc, ...rest } = props;
						return (
							<li
								key={key}
								{...rest}
								style={{ listStyle: "none" }}
								onMouseDown={(e) => {
									rest.onMouseDown?.(e as React.MouseEvent<HTMLLIElement>);
									e.preventDefault();
									e.stopPropagation();
									suppressNextCloseRef.current = true;
									toggleGroupExpand(option.groupKey);
								}}
							>
								<ListItemButton
									component="div"
									sx={{ pl: 3, py: 0.5 }}
								>
									<Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
										+ Show {option.remaining} remaining field{option.remaining !== 1 ? "s" : ""}
									</Typography>
								</ListItemButton>
							</li>
						);
					}
					if (option.kind === "table") {
						const { key, onClick: _oc, ...rest } = props;
						return (
							<li
								key={key}
								{...rest}
								style={{ listStyle: "none" }}
								onMouseDown={(e) => {
									rest.onMouseDown?.(e as React.MouseEvent<HTMLLIElement>);
									e.preventDefault();
									e.stopPropagation();
									suppressNextCloseRef.current = true;
									handleTableClick(option.table, option.groupKey);
								}}
							>
								<ListItemButton
									component="div"
									selected={activeTable?.table.value === option.table.value}
									sx={{ pl: 3, py: 0.75 }}
								>
									<ListItemText
										primary={<Typography variant="body2">{option.table.label}</Typography>}
									/>
									<ChevronRight fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
								</ListItemButton>
							</li>
						);
					}
					if (option.kind === "screenLocation") {
						const { key, onClick: _oc, ...rest } = props;
						const locId = String(option.location.id);
						return (
							<li
								key={key}
								{...rest}
								style={{ listStyle: "none" }}
								onMouseDown={(e) => {
									rest.onMouseDown?.(e as React.MouseEvent<HTMLLIElement>);
									e.preventDefault();
									e.stopPropagation();
									suppressNextCloseRef.current = true;
									void handleScreenLocationClick(option.location, option.groupKey);
								}}
							>
								<ListItemButton
									component="div"
									selected={
										activeScreenLocation != null &&
										String(activeScreenLocation.location.id) === locId
									}
									sx={{ pl: 3, py: 0.75 }}
								>
									<ListItemText
										primary={<Typography variant="body2">{option.location.name}</Typography>}
									/>
									<ChevronRight fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
								</ListItemButton>
							</li>
						);
					}
					const { key, ...rest } = props;
					return (
						<li key={key} {...rest} style={{ listStyle: "none" }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									pl: 3,
									pr: 2,
									py: 0.75,
									minHeight: 40,
									width: "100%",
									boxSizing: "border-box",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
									<Typography variant="body2" component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{option.option.label}
									</Typography>
									{option.option.dataType && (
										<Typography
											component="span"
											variant="caption"
											sx={{
												ml: "auto",
												flexShrink: 0,
												px: 0.875,
												py: 0.25,
												borderRadius: 1,
												backgroundColor: "rgba(2, 213, 171, 0.10)",
												border: "1px solid rgba(2, 213, 171, 0.32)",
												color: "rgba(13, 31, 48, 0.78)",
												fontWeight: 600,
												fontSize: "0.7rem",
												lineHeight: 1.4,
												whiteSpace: "nowrap",
												letterSpacing: "0.01em",
											}}
										>
											{option.option.dataType}
										</Typography>
									)}
								</Box>
							</Box>
						</li>
					);
				}}
			/>
		</FormEntityListboxContext.Provider>
	);
};

export default FormEntitySelect;
